import { InvoiceFileNameEnum, ServiceTypeEnum } from '@sr-sdks/api-sdk-axios';
import { Button, Icon, SR_ICONS } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { paidInvoicesParams, useInvoices } from '../api/payments/invoiceApi';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import { SEARCH_KEYS } from '../constant/SearchKeys';
import { msr3StatementSearchPlaceholder } from '../constant/paymentsSearchPlaceholders';
import { useDebouncedUrlParam } from '../hooks/useDebouncedUrlParam';
import { useTablePaginationState } from '../hooks/useTablePaginationState';
import EmptyValueErrorLayout from '../pages/Workspace/EmptyValueErrorLayout';
import downloadApi from '../utils/api/downloadApi';
import { isCsvExportDisabled } from '../utils/csv/csvExport';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { toDisbursementSavedFileName } from '../utils/helpers/statementFiles';
import { AppSearchInput } from './AppSearchInput';
import './AppTable.css';
import AppTableV2, { AppTableV2HeaderCell as TH } from './AppTableV2';
import { ServiceBadge } from './Badges/ServiceBadge';
import CurrencyFormatter from './CurrencyFormatter';
import DateFormatter from './DateFormatter';
import { DownloadCsvButton } from './DownloadCsvButton';
import { PageHeaderActions } from './PageHeaderActionsSlot';
import {
  fetchStatementsCsvPage,
  statementsCsvColumns,
} from './statementsCsvSpec';

// All payment statements are served from the same disbursement PDF; hoisted so
// the download handler can stay free of render-time dependencies.
const STATEMENT_FILE_NAME = InvoiceFileNameEnum.DisbursementStatementPdf;

const DEFAULT_SORT_BY = 'invoiceDate:desc';

interface MSR3StatementTableProps {
  /**
   * Offers a CSV download of this table's rows in the page header. Only the
   * Payments pages pass it; staff, title-summary and collection-account uses
   * render no button.
   */
  csv?: { fileLabel: string; label: string };
  isCollectionAccountMode?: boolean;
  isStaffView?: boolean;
  /**
   * URL query param this table reads/writes its page number from. Owned by the
   * caller so each page gets a distinct key and paginating one page never
   * bleeds into another's page state.
   */
  paginationKey: string;
  /**
   * Service types to display in this statement table. Forwarded to the
   * invoice `findAll` filter so each Payments page (Disbursements, Residuals)
   * can scope the table to the relevant service types.
   */
  serviceTypes?: ServiceTypeEnum[];
  /**
   * When true (and not a staff view), an empty result renders the rich
   * `EmptyValueErrorLayout` instead of the plain in-table empty message.
   */
  showRichEmptyState?: boolean;
  titleId?: string;
  workspaceId?: string;
}

/**
 * Reusable statements table for the MSR3 Payments pages. Renders paid invoices
 * for a workspace, optionally filtered by one or more service types.
 *
 * Data is fetched via React Query (`useInvoices`), which keys the cache on the
 * full param set — so each `(workspaceId, serviceType, page, sortBy)` combination
 * is its own entry and one Payments page can never paint another's rows.
 */
export const MSR3StatementTable = ({
  csv,
  isCollectionAccountMode = false,
  isStaffView = false,
  paginationKey,
  serviceTypes,
  showRichEmptyState,
  titleId,
  workspaceId,
}: MSR3StatementTableProps) => {
  const { handlePageRequest, page, search, setSearchAndResetPage, sortBy } =
    useTablePaginationState({
      defaultSortBy: DEFAULT_SORT_BY,
      paginationKey,
      searchKey: SEARCH_KEYS.STATEMENT_SEARCH,
    });
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    null | string
  >(null);

  const { data: workspace } = useQuery(queryOptionsGetWorkspace(workspaceId));
  const isAgent = workspaceTypeIsAgent(workspace);
  const searchPlaceholder = msr3StatementSearchPlaceholder(isAgent);

  // The filters alone: the CSV export supplies its own page and pageSize, so
  // the file and the table send the same query.
  const statementsParams = {
    beneficiaryTitleId: titleId,
    beneficiaryWorkspaceId: workspaceId,
    searchTerm: search,
    serviceType: serviceTypes,
    sortBy,
  };

  const invoicesQuery = useInvoices(
    paidInvoicesParams({ ...statementsParams, page, pageSize: 10 }),
    { enabled: !!workspaceId },
  );

  const {
    input: searchInput,
    isSearchFetching,
    setInput: setSearchInput,
  } = useDebouncedUrlParam(search, setSearchAndResetPage, {
    queryStatus: invoicesQuery,
  });

  const invoices = invoicesQuery.data?.items ?? [];
  const paginationMeta = invoicesQuery.data?.meta ?? null;

  const handleDownloadDocument = useCallback(async (invoiceId: string) => {
    setDownloadingInvoiceId(invoiceId);

    try {
      const paymentInvoiceBlob = await downloadApi.paymentStatement(
        STATEMENT_FILE_NAME,
        invoiceId,
      );

      downloadApi.downloadFile(
        paymentInvoiceBlob,
        toDisbursementSavedFileName(invoiceId, STATEMENT_FILE_NAME),
      );
    } catch {
      // Swallowed intentionally: download errors surface via the global ErrorModal.
    } finally {
      setDownloadingInvoiceId(null);
    }
  }, []);

  // Shares the search and sort the on-screen query uses, so the file matches
  // the rows the user is looking at. Disabled while the rows on screen are not
  // for the current params, or there is nothing to export.
  const csvButton = csv && (
    <PageHeaderActions>
      <DownloadCsvButton
        label={csv.label}
        columns={statementsCsvColumns}
        fetchPage={(exportPage, pageSize, signal) =>
          fetchStatementsCsvPage(statementsParams, exportPage, pageSize, signal)
        }
        fileLabel={csv.fileLabel}
        isDisabled={isCsvExportDisabled(
          invoicesQuery,
          paginationMeta?.totalItems,
        )}
        rowCount={paginationMeta?.totalItems ?? 0}
      />
    </PageHeaderActions>
  );

  // Only short-circuit the whole table (incl. pagination) on the first page.
  // On later pages an empty result must still render the table so the user can
  // page back; otherwise a stale/too-high page number traps them on a dead end.
  // A no-result *search* must keep the table (and its search field) on screen
  // so the user can clear/retry — falling through to the in-table empty state.
  if (
    showRichEmptyState &&
    !isStaffView &&
    !search &&
    invoicesQuery.isSuccess &&
    invoices.length === 0 &&
    page === 1
  ) {
    return (
      <>
        {csvButton}
        <EmptyValueErrorLayout contentType="payment" />
      </>
    );
  }

  const table = (
    <AppTableV2
      id="statements-table"
      customEmptyTable={{
        description: '',
        ...(!isStaffView &&
          showRichEmptyState && { icon: SR_ICONS.folderOpen }),
        title: 'No Statements Found',
      }}
      filterElement={
        <AppSearchInput
          classes={{ wrapper: 'sm:w-96' }}
          isFetching={isSearchFetching}
          placeholder={searchPlaceholder}
          value={searchInput}
          onChange={setSearchInput}
        />
      }
      headerRowElement={
        <tr>
          <TH sortByProperty={'invoiceDate'}>Date</TH>
          <TH>Payout Reference ID</TH>
          {!isCollectionAccountMode && <TH>Title</TH>}
          {isAgent && <TH>Client</TH>}
          {!isCollectionAccountMode && <TH>Service</TH>}
          <TH className="!text-right" sortByProperty={'amountNet'}>
            Amount
          </TH>
          <TH className="!text-center">Currency</TH>
          <TH hideLabel>Action</TH>
        </tr>
      }
      isLoading={invoicesQuery.isPending}
      pageRequestDeps="pageAndSort"
      paginationKey={paginationKey}
      paginationMeta={paginationMeta}
      onPageRequest={handlePageRequest}
    >
      {invoices.map((invoice) => (
        <tr key={invoice._id}>
          <td>
            <DateFormatter dateString={invoice.invoiceDate} />
          </td>
          <td className="whitespace-nowrap">{invoice.payoutRefNo || '—'}</td>
          {!isCollectionAccountMode && (
            <td>{invoice.beneficiaryTitleName || 'Unknown Title'}</td>
          )}
          {isAgent && (
            <td>{invoice.beneficiaryClientName || 'Unknown Client'}</td>
          )}
          {!isCollectionAccountMode && (
            <td>
              <ServiceBadge serviceType={invoice.serviceType} />
            </td>
          )}
          <td className="!text-right">
            <CurrencyFormatter amount={invoice.amountNet} />
          </td>
          <td className="!text-center">{invoice.currency}</td>
          <td className="!text-center">
            <Button
              className="h-8 w-8"
              icon={<Icon icon="download" weight="fill" />}
              loading={downloadingInvoiceId === invoice._id}
              size="icon"
              onClick={() => handleDownloadDocument(invoice._id)}
            >
              <span className="sr-only">Download</span>
            </Button>
          </td>
        </tr>
      ))}
    </AppTableV2>
  );

  return (
    <>
      {csvButton}
      {table}
    </>
  );
};
